import { validatePresence } from 'ember-changeset-validations/validators';
import { isNumber } from 'donor-elf-web/lib/string-utils';
import {
  type Operation,
  OperationsWithoutVal,
  type PropertyFilterInfo,
} from 'donor-elf-web/lib/filter-utils-new';
import { assignSingleProperty } from 'donor-elf-web/lib/object-utils';
import { isEmpty, isPresent } from '@ember/utils';
import type { ValidationResult } from 'ember-changeset/types';

export default function validateFilter(
  availablePropertyFilters: PropertyFilterInfo[],
) {
  let getFilterInfo = (content: any, changes: any) => {
    let propertyName = assignSingleProperty(
      content,
      changes,
      'propertyName',
    ) as string | undefined;
    return availablePropertyFilters.find(
      (x) => x.propertyName === propertyName,
    );
  };

  let validation: any = {
    propertyName: validatePresence({
      presence: true,
      message: "Can't be blank",
    }),
    operation: (
      _key: string,
      newValue: string,
      _oldValue: string,
      changes: any,
      content: any,
    ): ValidationResult => {
      let filterInfo = getFilterInfo(content, changes);
      if (filterInfo?.propertyType === 'no-operation') {
        if (isPresent(newValue)) {
          ('No operation property types should not have an operation');
        }
      } else {
        if (isEmpty(newValue)) {
          return "Can't be blank";
        }
      }
      return true;
    },
    val: (
      _key: string,
      newValue: string,
      _oldValue: string,
      changes: any,
      content: any,
    ): ValidationResult => {
      let filterInfo = getFilterInfo(content, changes);
      let operation = assignSingleProperty(content, changes, 'operation') as
        | Operation
        | undefined;
      if (filterInfo && operation) {
        if (
          filterInfo.propertyType !== 'giftHistory' &&
          filterInfo.propertyType !== 'distanceFrom' &&
          !(filterInfo.propertyType === 'date' && operation === '><')
        ) {
          if (OperationsWithoutVal[operation]) {
            if (isPresent(newValue)) {
              return 'Supposed to be blank';
            }
          } else {
            if (isEmpty(newValue)) {
              if (filterInfo?.propertyType === 'list') {
                return 'Select one or more options';
              } else {
                return "Can't be blank";
              }
            }
            if (filterInfo?.propertyType === 'number') {
              if (!isNumber(newValue)) {
                return 'Must be a number (no comma or $)';
              }
            }
          }
        }
      }
      return true;
    },
    distanceFromDistance: (
      _key: string,
      newValue: string,
      _oldValue: string,
      changes: any,
      content: any,
    ): ValidationResult => {
      const filterInfo = getFilterInfo(content, changes);

      if (filterInfo?.propertyType === 'distanceFrom') {
        if (isEmpty(newValue)) {
          return "Can't be blank";
        }
      }

      return true;
    },
    distanceFromLocation: (
      _key: string,
      newValue: string,
      _oldValue: string,
      changes: any,
      content: any,
    ): ValidationResult => {
      const filterInfo = getFilterInfo(content, changes);

      if (filterInfo?.propertyType === 'distanceFrom') {
        if (isEmpty(newValue)) {
          return "Can't be blank";
        }
      }

      return true;
    },
    giftHistoryVal: (
      _key: string,
      newValue: string,
      _oldValue: string,
      changes: any,
      content: any,
    ): ValidationResult => {
      let operation = assignSingleProperty(content, changes, 'operation') as
        | Operation
        | undefined;
      if (operation !== 'absent') {
        if (getFilterInfo(content, changes)?.propertyType === 'giftHistory') {
          if (isEmpty(newValue)) {
            return "Can't be blank";
          }
          if (!isNumber(newValue)) {
            return 'Must be a number (no comma or $)';
          }
        }
      }
      return true;
    },
    giftHistoryValType: (
      _key: string,
      newValue: string,
      _oldValue: string,
      changes: any,
      content: any,
    ): ValidationResult => {
      let operation = assignSingleProperty(content, changes, 'operation') as
        | Operation
        | undefined;
      if (operation !== 'absent') {
        if (getFilterInfo(content, changes)?.propertyType === 'giftHistory') {
          if (isEmpty(newValue)) {
            return "Can't be blank";
          }
        }
      }
      return true;
    },
    giftHistoryFrom: (
      _key: string,
      newValue: string,
      _oldValue: string,
      changes: any,
      content: any,
    ): ValidationResult => {
      if (getFilterInfo(content, changes)?.propertyType === 'giftHistory') {
        if (isEmpty(newValue)) {
          return "Can't be blank";
        }
      }
      return true;
    },
    giftHistoryTo: (
      _key: string,
      newValue: string,
      _oldValue: string,
      changes: any,
      content: any,
    ): ValidationResult => {
      if (getFilterInfo(content, changes)?.propertyType === 'giftHistory') {
        if (isEmpty(newValue)) {
          return "Can't be blank";
        }
      }
      return true;
    },
    dateBetweenAfterVal: (
      _key: string,
      newValue: string,
      _oldValue: string,
      changes: any,
      content: any,
    ): ValidationResult => {
      let filterInfo = getFilterInfo(content, changes);
      let operation = assignSingleProperty(content, changes, 'operation') as
        | Operation
        | undefined;
      if (filterInfo?.propertyType === 'date' && operation === '><') {
        if (isEmpty(newValue)) {
          return "Can't be blank";
        }
      }
      return true;
    },
    dateBetweenBeforeVal: (
      _key: string,
      newValue: Date,
      _oldValue: string,
      changes: any,
      content: any,
    ): ValidationResult => {
      let filterInfo = getFilterInfo(content, changes);
      let operation = assignSingleProperty(content, changes, 'operation') as
        | Operation
        | undefined;
      if (filterInfo?.propertyType === 'date' && operation === '><') {
        if (isEmpty(newValue)) {
          return "Can't be blank";
        }
        let dateBetweenAfterVal = assignSingleProperty(
          content,
          changes,
          'dateBetweenAfterVal',
        ) as Date | undefined;
        if (dateBetweenAfterVal && dateBetweenAfterVal >= newValue) {
          return 'Must be after the other date';
        }
      }
      return true;
    },
  };
  return validation;
}
